<template>
  <div class="cp-sidebar__main" :class="{'loading': loading, 'legacy-order-tracking': isLegacy}">
    <div class="order-tracking">
      <ProcessingLoader v-if="loading" class="loader" />
      <!-- Main Content -->
      <div class="order-tracking__content" v-if="item && itemFulfillments.length" :class="{'d-none': loading}">
        <!-- Left Panel - Tracking Details -->
        <div ref="leftpanel" class="order-tracking__left-panel" v-if="tracking">
          <div ref="trackingInfo" class="tracking-info">
            <!-- Tracking Header -->
            <div class="tracking-info__carrier">
              <div v-if="tracking.carrier" class="tracking-info__carrier-logo-container">
                <img :src="require(`@/assets/images/trackinglogos/${(tracking.carrier || '').replaceAll(' ', '')}.png`)" class="carrier-logo" />
              </div>
              <div class="tracking-info__tracking-number">
                <div>Tracking#</div>
                <div class="tracking-number" :class="{'no-cursor': itemFulfillments.length <= 1}" v-for="fulfillment in itemFulfillments" v-bind:key="'track-'+fulfillment.tracking_number" @click="updateSelectedFulfillment(fulfillment)">
                  <span>{{ fulfillment.tracking_number }}</span>
                  <svg v-if="itemFulfillments.length > 1 && selectedFulfillment.id === fulfillment.id" class="d-none d-lg-block" xmlns="http://www.w3.org/2000/svg" width="9" height="15" viewBox="0 0 9 15" fill="none">
                    <path d="M1.705 0.243164L0 1.94816L5.54 7.50016L0 13.0522L1.705 14.7572L8.962 7.50016L1.705 0.243164Z" fill="#1C1C1C"/>
                  </svg>
                </div>
              </div>
              <svg @click.stop="toggleScrollbar()" xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46" fill="none" class="d-block d-md-none cursor-pointer">
                <g filter="url(#filter0_d_2439_55251)">
                  <g filter="url(#filter1_d_2439_55251)">
                    <circle cx="23" cy="19" r="19" fill="#F4F4F4"/>
                  </g>
                  <path d="M30 26L16 12L30 26ZM30 12L16 26L30 12Z" fill="#FAFAFA"/>
                  <path d="M30 26L16 12M30 12L16 26" stroke="#797979" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                  <filter id="filter0_d_2439_55251" x="0" y="0" width="46" height="46" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                    <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                    <feOffset dy="4"/>
                    <feGaussianBlur stdDeviation="2"/>
                    <feComposite in2="hardAlpha" operator="out"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"/>
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2439_55251"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2439_55251" result="shape"/>
                  </filter>
                </defs>
              </svg>
            </div>
            <div ref="trackingInfoHeader" class="tracking-info__header">
              <div v-if="!hideEstimatedDate">
                <div class="tracking-info__wrapper">
                  <span class="tracking-info__wrapper__title">Estimated Date</span>
                  <span class="tracking-info__wrapper__heading" v-if="!estimatedDeliveryDate">Estimating Delivery...</span>
                  <span class="tracking-info__wrapper__description" :class="{'estimated_date': estimatedDeliveryDate}">
                    {{ estimatedDateDescriptionText }}
                  </span>
                </div>
              </div>

              <div class="tracking-info__delivery_status_container">
                <div class="tracking-info__wrapper" v-if="latestTrackingEvent">
                  <span class="tracking-info__wrapper__heading status-badge" :class="getStatusClass(tracking.status)">{{ statusText }}</span>
                  <span class="tracking-info__wrapper__description">
                    <span v-if="isDelivered && latestTrackingEvent">{{latestTrackingEvent.date}}</span>
                    <span v-else>{{ statusDescriptionText }}</span>
                  </span>
                </div>
              </div>
            </div>

            <!-- Shipping Details Timeline -->
            <div class="tracking-info__timeline">
              <div class="timeline-header">
                Shipping Details
              </div>
              <div ref="timelineContainer" class="timeline-container">
                <div class="timeline">
                  <div
                    v-for="[status, item] in trackingTimeline.entries()"
                    :key="'status-' + status"
                    :ref="'item-' + status"
                    class="timeline-item"
                  >
                    <!-- Vertical line and dot -->
                    <div class="timeline-marker" >
                      <div
                        class="timeline-line"
                        :class="{
                          active: isActiveStatus(status),
                          expanded: item.expanded
                        }"
                      ></div>
                      <div
                        class="timeline-dot"
                        :class="{
                          active: isActiveStatus(status),
                          completed: isCompletedStatus(status)
                        }"
                      ></div>
                    </div>

                    <!-- Content -->
                    <div class="timeline-content">
                      <div class="timeline-content__heading" @click="toggleDetail(status)">
                        <div class="timeline-content__heading__container">
                          <h3>{{ getStatusText(status) }}</h3>
                          <svg v-if="item.events && item.events.length" :class="{'rotated': item.expanded}" xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8" fill="none">
                            <path d="M1 1.5L6 6.5L11 1.5" stroke="#4E6574" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                        </div>
                        <p class="location">
                          <span v-if="item.location">{{ item.location }}</span>
                          <span v-if="item.location && item.date"> - </span>
                          <span v-if="item.date">{{ item.date }}</span>
                        </p>
                      </div>
                      <div v-if="item.events && item.events.length && item.expanded" class="events">
                        <div v-for="(event, i) in item.events" :key="i" class="event">
                          <ul>
                            <li>
                              <div class="event-time">{{ event.date }}</div>
                              <div class="event-description">{{ event.message }}</div>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Button -->
            <div class="tracking-info__actions" v-if="session && session.can_undo">
              <button class="btn-base input-choice-btn input-btn stroke-2x" @click="toggleScrollbar()">
                Return to Order
              </button>
            </div>
          </div>
        </div>

        <!-- Right Panel - Map -->
        <div class="order-tracking__right-panel">
          <div class="map-container"><div id="map" ref="mapContainer"></div></div>
        </div>
      </div>
      <div class="centered-block" v-else-if="!loading && !tracking">
        <div class="step-title">Invalid tracking ID or couldn't find any data</div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState} from 'vuex'
import {formatDateString, getShortTime} from '@/utils'
import {
  Countries, TrackingStatus, TrackingStatusDescriptionStrings,
  TrackingStatusStrings, TrackingStatusClass, TrackingStatusMissingDeliverDateStrings
} from '@/constants'
import axios from 'axios'
import ProcessingLoader from '@/components/ProcessingLoader.vue'

export default {
  name: 'OrderTracking',
  components: {ProcessingLoader},
  props: {
    item: {
      type: Object,
      required: true
    },
    fulfillments: {
      type: Array,
      default: () => []
    }
  },
  data: function() {
    return {
      map: null,
      tracking: null,
      allTrackingInfo: {},
      trackingTimeline: null,
      selectedFulfillment: null,
      itemFulfillments: [],
      loading: true,
      infoWindows: [],
      activeInfoWindow: null,
      latestTrackingEvent: null,
      routeCrossesAntimeridian: false
    }
  },
  computed: {
    ...mapState(['styleVariables', 'session']),
    hideEstimatedDate: function() {
      return [
        TrackingStatus.OutForDelivery, TrackingStatus.AvailableForPickup,
        TrackingStatus.InTransit, TrackingStatus.PreTransit
      ].indexOf(this.tracking.status) === -1
    },
    statusText: function() {
      return TrackingStatusStrings[this.tracking.status]
    },
    statusDescriptionText: function() {
      return TrackingStatusDescriptionStrings[this.tracking.status]
    },
    estimatedDateDescriptionText: function() {
      return this.estimatedDeliveryDate ? this.estimatedDeliveryDate : TrackingStatusMissingDeliverDateStrings[this.tracking.status]
    },
    isDelivered: function() {
      return this.tracking && this.tracking.status === TrackingStatus.Delivered
    },
    isOutForDelivery: function() {
      return this.tracking && this.tracking.status === TrackingStatus.OutForDelivery
    },
    estimatedDeliveryDate: function() {
      let estimatedDeliveryDate = this.tracking.est_delivery_date
      return estimatedDeliveryDate ? formatDateString(estimatedDeliveryDate) : null
    },
    isLegacy: function() {
      return this.session && this.session.is_legacy && false
    },
    deliveryAddress: function() {
      let address = null

      if (!this.isDelivered && !this.isOutForDelivery && this.session && this.session.customer) {
        const customer = this.session.customer
        if (customer.ship_state && customer.ship_city) {
          address = {
            address: customer.ship_address1,
            city: customer.ship_city,
            state: customer.ship_state,
            country: customer.ship_country
          }
          address['location'] = `${address.address}, ${address.city}, ${address.state} ${address.country}`
        }
      }

      return address
    },
    isNotFoundTracking: function() {
      return this.tracking && this.tracking.status === TrackingStatus.NotFound
    }
  },
  methods: {
    isActiveStatus: function(status) {
      return !this.isNotFoundTracking && this.latestTrackingEvent && status <= this.latestTrackingEvent.status
    },
    isCompletedStatus: function(status) {
      return this.isDelivered || (!this.isNotFoundTracking && this.latestTrackingEvent && status < this.latestTrackingEvent.status)
    },
    getIcon: function(isStart, isLast) {
      if (isStart) {
        return {
          url: 'data:image/svg+xml;utf-8,' + encodeURIComponent(`
            <svg width="54" height="54" viewBox="0 0 54 54" xmlns="http://www.w3.org/2000/svg">
              <path opacity="0.35" d="M27.0573 53.4934C41.6613 53.4934 53.5003 41.6545 53.5003 27.0504C53.5003 12.4464 41.6613 0.607422 27.0573 0.607422C12.4532 0.607422 0.614258 12.4464 0.614258 27.0504C0.614258 41.6545 12.4532 53.4934 27.0573 53.4934Z" fill="${this.styleVariables['theme_color']}"/>
              <path d="M27.0591 39.7003C34.0444 39.7003 39.7071 34.0376 39.7071 27.0523C39.7071 20.067 34.0444 14.4043 27.0591 14.4043C20.0738 14.4043 14.4111 20.067 14.4111 27.0523C14.4111 34.0376 20.0738 39.7003 27.0591 39.7003Z" fill="#71AC00"/>
              <path d="M27.0591 38.2003C33.216 38.2003 38.2071 33.2092 38.2071 27.0523C38.2071 20.8954 33.216 15.9043 27.0591 15.9043C20.9023 15.9043 15.9111 20.8954 15.9111 27.0523C15.9111 33.2092 20.9023 38.2003 27.0591 38.2003Z" fill="${this.styleVariables['theme_color']}" stroke="white" stroke-width="3"/>
            </svg>
          `),
          anchor: new window.google.maps.Point(27, 27)
        }
      }

      if (isLast) {
        return {
          url: 'data:image/svg+xml;utf-8,' + encodeURIComponent(`
          <svg width="41" height="56" viewBox="0 0 41 56" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.1 0C14.7712 0.00661555 9.66251 2.12641 5.89446 5.89446C2.12641 9.66251 0.00661555 14.7712 0 20.1C0 33.847 17.985 54.041 18.75 54.894C18.9197 55.083 19.1274 55.2341 19.3593 55.3375C19.5913 55.441 19.8425 55.4944 20.0965 55.4944C20.3505 55.4944 20.6017 55.441 20.8337 55.3375C21.0656 55.2341 21.2733 55.083 21.443 54.894C22.208 54.044 40.193 33.852 40.193 20.1C40.1864 14.7724 38.0676 9.66478 34.301 5.89693C30.5345 2.12908 25.4276 0.00846713 20.1 0ZM20.1 30.208C18.1001 30.208 16.1451 29.6149 14.4823 28.5038C12.8195 27.3926 11.5236 25.8133 10.7584 23.9656C9.99318 22.1179 9.79312 20.0848 10.1835 18.1233C10.5739 16.1619 11.5372 14.3603 12.9515 12.9464C14.3659 11.5325 16.1677 10.5697 18.1293 10.1799C20.0908 9.79012 22.1239 9.99078 23.9714 10.7565C25.8189 11.5223 27.3978 12.8187 28.5084 14.4818C29.6191 16.145 30.2116 18.1001 30.211 20.1C30.2102 22.7811 29.1446 25.3521 27.2485 27.2476C25.3524 29.1431 22.7811 30.208 20.1 30.208Z" fill="${this.styleVariables['theme_color']}"/>
          </svg>
        `)
        }
      }
      return {
        url: 'data:image/svg+xml;utf-8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
            <path d="M12.648 23.796C18.8049 23.796 23.796 18.8049 23.796 12.648C23.796 6.49113 18.8049 1.5 12.648 1.5C6.49113 1.5 1.5 6.49113 1.5 12.648C1.5 18.8049 6.49113 23.796 12.648 23.796Z" fill="${this.styleVariables['theme_color']}" stroke="white" stroke-width="3"/>
          </svg>
        `),
        anchor: new window.google.maps.Point(13, 13)
      }
    },
    initializeTimeline: function() {
      const defaultTracking = {
        date: null,
        scan_date: null,
        location: null,
        status: null,
        message: null,
        expanded: false,
        events: []
      }
      this.trackingTimeline = new Map([
        [TrackingStatus.Delivered, {
          ...defaultTracking,
          status: TrackingStatus.Delivered,
          location: this.deliveryAddress && this.deliveryAddress.location
        }],
        [TrackingStatus.OutForDelivery, {...defaultTracking, status: TrackingStatus.OutForDelivery}],
        [TrackingStatus.InTransit, {...defaultTracking, status: TrackingStatus.InTransit}],
        [TrackingStatus.PreTransit, {...defaultTracking, status: TrackingStatus.PreTransit}]
      ])
    },
    closeAllInfoWindows: function() {
      this.infoWindows.forEach(infoWindow => {
        infoWindow.close()
      })
      this.activeInfoWindow = null
    },
    toggleScrollbar: function() {
      if (this.isLegacy) {
        this.$emit('clear')
      } else {
        this.$emit('clear')
      }
    },
    getStatusText: function(status) {
      return TrackingStatusStrings[status]
    },
    toggleDetail: function(status) {
      const selectedDetail = this.trackingTimeline.get(status)
      if (selectedDetail && selectedDetail.events && selectedDetail.events.length > 0) {
        // eslint-disable-next-line no-unused-vars
        for (const [_, detail] of this.trackingTimeline.entries()) {
          if (detail.status === status) {
            detail.expanded = !detail.expanded
          } else {
            detail.expanded = false
          }
        }
        this.trackingTimeline = new Map(this.trackingTimeline)

        this.$nextTick(() => {
          const refs = this.$refs[`item-${status}`]
          const element = Array.isArray(refs) ? refs[0] : refs
          if (element && this.$refs.timelineContainer) {
            const isSmallScreen = window.innerWidth < 768
            const container = isSmallScreen ? this.$refs.leftpanel : this.$refs.timelineContainer
            const trackingInfoHeader = this.$refs.trackingInfoHeader
            const elementTop = element.offsetTop
            const scrollOffset = elementTop + (isSmallScreen ? trackingInfoHeader.offsetHeight - 40 : 10)
            container.scrollTo({
              top: (this.trackingTimeline.values().every((timeline) => !timeline.expanded)) ? 0 : scrollOffset,
              behavior: 'smooth'
            })
          }
        })
      }
    },
    extractUniqueLocationsWithMessages: function(events) {
      let result = []
      if (events && events.length > 0) {
        const countries = Object.values(Countries).concat(Object.keys(Countries)).concat(['usa', 'united states', 'hk', 'cn', 'china', 'hong kong'])
          .map(country => country.toLowerCase())

        let currentLocation = null
        let locationGroup = null

        // Sort events by time in descending order (most recent last)
        const sortedEvents = [...events].sort((a, b) => {
          return new Date(a.time_iso) - new Date(b.time_iso)
        })

        sortedEvents.forEach((event) => {
          const eventLocation = event.location
          const eventDate = formatDateString(event.scan_date, false, true)
          let onlyCountry = countries.indexOf(eventLocation.toLowerCase()) > -1

          if (!onlyCountry || event.status === TrackingStatus.Delivered) {
            // If location changed, save the previous location group and start a new one
            if (eventLocation !== currentLocation) {
              if (currentLocation !== null && locationGroup !== null) {
                result.push(locationGroup)
              }

              currentLocation = eventLocation
              locationGroup = {
                location: eventLocation,
                dates: []
              }
            }
          }

          if (locationGroup) {
            // Check if this date already exists in the current location group
            let dateGroup = locationGroup.dates.find(dg => dg.date === eventDate)

            if (!dateGroup) {
              dateGroup = {
                date: eventDate,
                messages: []
              }
              locationGroup.dates.push(dateGroup)
            }

            dateGroup.messages.push({
              message: event.message,
              time: getShortTime(event.scan_date)
            })
          }
        })

        if (currentLocation !== null && locationGroup !== null) {
          result.push(locationGroup)
        }
      }

      return result
    },
    getUniqueLocations: function() {
      if (!this.tracking || !this.trackingTimeline) {
        return []
      }
      return this.extractUniqueLocationsWithMessages(this.tracking.details)
    },
    geocodeAddress: function(address) {
      return new Promise((resolve, reject) => {
        const geocoder = new window.google.maps.Geocoder()
        geocoder.geocode({address}, (results, status) => {
          if (status === 'OK' && results[0]) {
            resolve({
              address: address.length <= results[0].formatted_address ? results[0].formatted_address : address,
              coordinates: {
                lat: results[0].geometry.location.lat(),
                lng: results[0].geometry.location.lng()
              }
            })
          } else {
            reject(status)
          }
        })
      })
    },
    initializeMap: function(centerPosition, positions) {
      const hasPositions = positions && positions.length > 0
      // Create the map
      this.map = new window.google.maps.Map(this.$refs.mapContainer, {
        center: centerPosition,
        zoom: positions.length > 1 ? 12 : 5,
        disableDefaultUI: hasPositions,
        fullscreenControl: hasPositions,
        zoomControl: hasPositions,
        mapTypeControl: hasPositions,
        gestureHandling: hasPositions ? 'auto' : 'none',
        keyboardShortcuts: hasPositions,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{visibility: 'off'}]
          }
        ]
      })
    },
    addClickHandler: function(marker, infoWindow) {
      // Add click event listener to the marker
      marker.addListener('click', () => {
        // Check if this info window is already open
        if (this.activeInfoWindow === infoWindow) {
          // If it's already open, close it
          this.closeAllInfoWindows()
        } else {
          // Close all info windows first
          this.closeAllInfoWindows()

          // Open this info window and set it as active
          infoWindow.open(this.map, marker)
          this.activeInfoWindow = infoWindow
        }
      })
    },
    addInfoWindowToMarker: function(position, mapMarker, markerInfo) {
      // Generate HTML for multiple messages
      let messagesHtml = ''

      if (markerInfo.dates) {
        messagesHtml = '<ul class="messages-items">'
        for (let detail of markerInfo.dates) {
          messagesHtml += `
            <li class="message-item">
              <p class="heading">${detail.date}</p>
          `

          detail.messages.forEach(item => {
            messagesHtml += `<p class="description">
              <b>${item.time}</b> -
              ${item.message}
            </p>`
          })

          messagesHtml += '</li>'
        }

        messagesHtml += '</ul>'
      }

      const infoWindow = new window.google.maps.InfoWindow({
        headerDisabled: true,
        content: `
            <div class="marker-address">
              <p class="heading">${markerInfo.location}</p>
              ${messagesHtml}
            </div>
        `,
        position: position
      })

      this.addClickHandler(mapMarker, infoWindow)
      // Store the info window in the array
      this.infoWindows.push(infoWindow)
    },
    addMarkerToMap: function(position, icon) {
      return new window.google.maps.Marker({
        position: position,
        map: this.map,
        icon: icon
      })
    },
    setAntimeridianRoute: function(coordinates) {
      for (let i = 1; i < coordinates.length; i++) {
        const lonDiff = coordinates[i].lng - coordinates[i - 1].lng
        if (Math.abs(lonDiff) > 180) {
          this.routeCrossesAntimeridian = true
          break
        }
      }
    },
    calculateCenterForAntimeridianRoute(positions) {
      if (this.routeCrossesAntimeridian) {
        let sumLat = 0
        let sumLng = 0

        for (let point of positions) {
          sumLat += point.lat
          sumLng += point.lng
        }

        const avgLat = sumLat / positions.length
        const avgLng = sumLng / positions.length

        return {lat: avgLat, lng: avgLng}
      } else if (positions.length) {
        return positions[0]
      }
    },
    normalizePolylinePath(points) {
      if (points && points.length > 1) {
        const normalizedPoints = [points[0]]
        for (let i = 1; i < points.length; i++) {
          const prevPoint = normalizedPoints[i - 1]
          const currentPoint = points[i]

          let adjustedLng = currentPoint.lng
          const lngDifference = adjustedLng - prevPoint.lng

          // If the difference is greater than 180 degrees, adjust to go the other way
          if (lngDifference > 180) {
            adjustedLng -= 360
          } else if (lngDifference < -180) {
            adjustedLng += 360
          }

          normalizedPoints.push({
            lat: currentPoint.lat,
            lng: adjustedLng
          })
        }
        return normalizedPoints
      }
      return points
    },
    addPathToMarkers: function(positions) {
      positions = this.normalizePolylinePath(positions)
      let paths = {}

      for (const [index] of positions.entries()) {
        if (index >= 0) {
          let positionPath = []
          let key = `${positions[index].lat}-${positions[index].lng}`
          if (index === 0) {
            positionPath = [positions[index]]
          } else {
            positionPath = [positions[index - 1], positions[index]]
            key += `${positions[index - 1].lat}-${positions[index - 1].lng}`
          }
          paths[key] = positionPath

          // Draw a path between markers
          const path = new window.google.maps.Polyline({
            path: positionPath,
            geodesic: !this.routeCrossesAntimeridian,
            strokeOpacity: 0,
            strokeWeight: 3,
            icons: [{
              icon: {
                path: 'M 0,-1 0,1',
                strokeOpacity: 1,
                scale: 4,
                strokeColor: this.styleVariables['theme_color']
              },
              offset: '0',
              repeat: '18px'
            }]
          })
          path.setMap(this.map)
        }
      }
    },
    addWindowResizeEventListener: function(positions, mapBounds, currentPosition) {
      window.addEventListener('resize', () => {
        const map = this.map
        if (map && positions.length > 1) {
          map.fitBounds(mapBounds)
          if (window.innerWidth < 992) {
            // Estimate InfoWindow dimensions
            const iwOuter = document.querySelector('.gm-style-iw')
            if (!iwOuter) return

            const iwHeight = iwOuter.offsetHeight
            const iwWidth = iwOuter.offsetWidth

            const projection = map.getProjection()
            if (projection) {
              const markerPos = projection.fromLatLngToPoint(currentPosition)
              const offsetY = (iwHeight / 2 + 10) / Math.pow(2, map.getZoom())
              const offsetX = (iwWidth / 2 + 10) / Math.pow(2, map.getZoom())
              const newCenter = new window.google.maps.Point(markerPos.x - offsetX, markerPos.y - offsetY)
              map.setCenter(projection.fromPointToLatLng(newCenter))
            }
          }
        }
      })
    },
    initMap() {
      let positions = []
      let locations = {}
      let markers = this.getUniqueLocations()

      const processAddresses = async() => {
        for (let marker of markers) {
          if (locations[marker.location]) {
            const position = { ...locations[marker.location] }
            positions.push(position)
          } else {
            try {
              const { address, coordinates } = await this.geocodeAddress(marker.location)
              marker.location = address
              positions.push(coordinates)
              locations[marker.location] = coordinates
            } catch (err) {
              console.warn('Geocode failed for', marker.location, err)
              marker.invalid = true
            }
          }
        }

        markers = markers.filter(marker => !marker.invalid)
        this.setAntimeridianRoute(positions)

        const center = this.calculateCenterForAntimeridianRoute(positions)
        // Once all are resolved
        this.initializeMap(positions.length > 0 ? center : { lat: 40.54241, lng: -103.5004173 }, positions)
        this.infoWindows = []

        positions.forEach((pos, index) => {
          const icon = this.getIcon(index === positions.length - 1, index === 0)
          const mapMarker = this.addMarkerToMap(pos, icon)
          this.addInfoWindowToMarker(pos, mapMarker, markers[index])
        })

        this.addPathToMarkers(positions)

        const bounds = new window.google.maps.LatLngBounds()
        positions.forEach(p => bounds.extend(p))
        if (positions.length > 1) {
          this.map.fitBounds(bounds)
        }

        this.addWindowResizeEventListener(positions, bounds, positions[0])
      }

      processAddresses()
    },
    getStatusClass: function(status) {
      return TrackingStatusClass[status]
    },
    getTrackingData() {
      if (this.item) {
        if (!this.fulfillments || !this.fulfillments.length) {
          this.loading = true
          axios.get(`/order_items/${this.item.id}/fulfillments/`).then(response => {
            const trackAbleFulfillments = response.data.filter(item => item.can_track)
            if (trackAbleFulfillments && trackAbleFulfillments.length) {
              this.itemFulfillments = response.data.filter((item) => item.can_track)
              this.updateSelectedFulfillment(trackAbleFulfillments[0])
            } else {
              this.loading = false
            }
          }, () => {
            this.loading = false
          })
        } else {
          this.itemFulfillments = this.fulfillments
          this.updateSelectedFulfillment(this.itemFulfillments[0])
        }
      }
    },
    prepareTrackingTimeline: function() {
      for (let [index, detail] of this.tracking.details.entries()) {
        let location = ''

        if (detail.city && detail.state) {
          location = detail.city + ', ' + detail.state + ', ' + detail.country
        }

        location = location || detail.location || ''
        let message = detail.message || ''

        if ([TrackingStatus.Unknown].indexOf(detail.status) > -1) {
          detail.status = TrackingStatus.InTransit
        }

        if ([TrackingStatus.ReturnToSender, TrackingStatus.Failure].indexOf(detail.status) > -1) {
          detail.status = TrackingStatus.OutForDelivery
        }

        if (detail.status === TrackingStatus.AvailableForPickup) {
          if (this.tracking.details.length > index) {
            detail.status = this.tracking.details[index + 1].status
          }
        }

        if (detail.status === TrackingStatus.InTransit) {
          message = message + (message.endsWith('in') ? ' ' : ', ') + location
        } else {
          message = location + (location ? ' - ' : '') + message
        }
        const trackingInfo = this.trackingTimeline.get(detail.status)
        let updateStatus = {...trackingInfo}

        if (!trackingInfo.scan_date) {
          updateStatus = {
            date: formatDateString(detail.scan_date),
            scan_date: detail.scan_date,
            location: location,
            status: detail.status,
            message: message,
            expanded: false,
            events: []
          }

          if (detail.status !== TrackingStatus.Delivered && message) {
            updateStatus['events'].push({
              location: location,
              scan_date: detail.scan_date,
              date: formatDateString(detail.scan_date),
              message: message
            })
          }
        } else {
          updateStatus['events'].push({
            ...(location ? {
              location: location,
              date: formatDateString(detail.scan_date)
            } : {}),
            scan_date: detail.scan_date,
            message: message
          })
        }
        this.trackingTimeline.set(detail.status, updateStatus)
      }

      if (this.trackingTimeline) {
        // eslint-disable-next-line no-unused-vars
        for (let [_, detail] of this.trackingTimeline.entries()) {
          if (detail && detail['events'].length) {
            detail['events'] = detail['events'].sort((a, b) => new Date(b.scan_date) - new Date(a.scan_date))
          }
          if (detail.scan_date && !this.latestTrackingEvent) {
            this.latestTrackingEvent = {...detail}
            detail.expanded = true
            this.trackingTimeline.set(detail.status, detail)
          }
        }
      }

      if (this.tracking.status === TrackingStatus.NotFound) {
        this.latestTrackingEvent = {
          date: formatDateString(this.tracking.date),
          scan_date: this.tracking.date,
          status: TrackingStatus.NotFound,
          message: this.tracking.message
        }
      }
    },
    prepareMarkerLocations: function() {
      this.tracking.details = this.tracking.details.map(detail => {
        let location = ''
        let country = detail.country || 'US'
        if (detail.city && detail.state) {
          location = detail.city + ', ' + detail.state + ' ' + country
        }
        detail.location = location || detail.location || ''
        return detail
      })
    },
    postFetchTrackingData() {
      try {
        this.initializeTimeline()
        this.prepareMarkerLocations()
        this.prepareTrackingTimeline()
        this.loading = false
        this.initMap()
      } catch (e) {
        console.error('GEOCODING ERROR:' + e)
      }
    },
    updateSelectedFulfillment(fulfillment) {
      if (!this.selectedFulfillment || this.selectedFulfillment.id !== fulfillment.id) {
        this.selectedFulfillment = fulfillment
        if (!this.allTrackingInfo[fulfillment.id.toString()]) {
          this.loading = true
          axios.get('/fulfillments/' + this.selectedFulfillment.id.toString() + '/').then(async(result) => {
            this.tracking = result.data.tracking
            this.allTrackingInfo[fulfillment.id.toString()] = this.tracking
            this.postFetchTrackingData()
          }, () => {
            this.tracking = {
              status: TrackingStatus.NotFound,
              details: []
            }
            this.allTrackingInfo[fulfillment.id.toString()] = this.tracking
            this.postFetchTrackingData()
            this.loading = false
          })
        } else {
          this.tracking = this.allTrackingInfo[fulfillment.id.toString()]
          this.postFetchTrackingData()
          this.loading = false
        }
      }
    }
  },
  mounted() {
    this.getTrackingData()
  }
}
</script>

<style scoped lang="less">
.order-tracking {
  width: 100%;
  display: flex;
  background: #FFFFFF;
  flex-direction: column;
  font-family: 'Open Sans', sans-serif;

  .centered-block {
    flex: 1;
  }

  &__content {
    display: flex;
    padding: 0;
    margin: 0 auto;
    width: 100%;
    flex-direction: column-reverse;
    background: #FFF;

    @media (min-width: 768px) {
      flex-direction: row;
    }
  }

  &__left-panel {
    flex: 1;
    max-width: none;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;

    @media (min-width: 768px) {
      max-width: 320px;
      height: auto;
      position: relative;   // keep for absolute child
      z-index: 2;
      border-radius: 0 20px 20px 0;
      background: #FFF;
      box-shadow: 0 -4px 6px 2px rgba(0, 0, 0, 0.05);
      overflow-y: hidden;
    }

    @media (min-width: 992px) {
      max-width: 344px;
    }
  }

  &__right-panel {
    position: relative;
    @media (min-width: 768px) {
      min-height: 300px;
      flex: 1;
      margin-left: -40px;
    }
  }
}

.tracking-info {
  padding: 0 0;
  border-radius: 20px 20px 0 0;
  background: #FFF;
  box-shadow: 0 -4px 6px 2px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 6;

  @media (min-width: 768px) {
    position: relative;
    top: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    border-radius: 0 20px 20px 0;
    background: #FFF;
    box-shadow: none;
  }

  &__header {
    margin-bottom: 20px;
    flex: 0 0 auto;
    padding-top: 120px;

    > div {
      display: flex;
      align-items: center;
      margin-bottom: 21px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    @media (min-width: 768px) {
      padding-top: 0;
    }
  }

  &__carrier {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 15px 20px;
    position: fixed;
    left: 0;
    right: 0;
    z-index: 2;
    background: #FFFFFF;
    box-shadow: 0 4px 6px 2px rgba(0, 0, 0, 0.03);

    &-logo-container {
      width: 52px;
      height: 52px;
      min-width: 70px;
      min-height: 70px;
      border-radius: 2px;

      .carrier-logo {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }

      @media (min-width: 575px) {
        width: 70px;
        height: 70px;
        min-width: 70px;
        min-height: 70px;
      }
    }

    @media (min-width: 768px) {
      position: relative;
      padding: 25px 20px;
      background: transparent;
      box-shadow: none;
    }
  }

  &__tracking-number {
    gap: 2px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    color: #4E6574;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    flex: 1;

    .tracking-number {
      color: #163549;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
      display: flex;
      align-items: center;
      justify-content: space-between;
      cursor: pointer;

      &.no-cursor {
        cursor: unset;
      }

      > svg {
        fill: #1C1C1C;
        justify-self: flex-end;
      }

      span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 70%;

        @media (min-width: 768px) {
          max-width: 180px;
        }
      }

      @media (min-width: 375px) {
        font-size: 14px;
      }

      @media (min-width: 575px) {
        font-size: 16px;
      }
    }
  }

  .tracking-info__wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0;
    flex: 1;
    padding: 0 20px;

    &__title {
      color: #4E6574;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }

    &__heading {
      color: #163549;
      font-size: 20px;
      font-style: normal;
      font-weight: 700;
      line-height: normal;

      &.success {
        color: #089B6B;
      }

      &.failure {
        color: #D9534F;
      }
    }

    &__description {
      color: #4E6574;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;

      &.estimated_date {
        color: #163549;
        font-size: 22px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
      }
    }
  }

  &__delivery_status_container {
    display: flex;
    align-items: center;
    gap: 16px;
    align-self: stretch;
    background: #FAFAFA;
    padding: 15px 0;
  }

  &__delivery, &__status {
    display: flex;
    flex-direction: column;
    justify-content: center;
    font-size: 14px;
    color: #5A5A5A;

    .delivery-date {
      font-weight: 600;
      color: #333333;
    }

    .status-badge {
      padding: 4px 12px;
      border-radius: 16px;
      font-size: 12px;
      font-weight: 500;

      &.out-for-delivery {
        background: #E8F5E8;
        color: #27AE60;
      }

      &.in-transit {
        background: #E3F2FD;
        color: #1976D2;
      }

      &.delivered {
        background: #E8F5E8;
        color: #27AE60;
      }

      &.pending {
        background: #FFF8E1;
        color: #F39C12;
      }

      &.failure {
        background: #FFF8E1;
        color: #ff0000;
      }
    }
  }

  &__timeline {
    margin-bottom: 40px;
    display: flex;
    flex-direction: column; /* allow nested scrollable child to expand */
    overflow: hidden;       /* prevent middle wrapper from overflowing */
    padding: 0 20px;

    .timeline-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: #163549;
      font-size: 18px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      margin-bottom: 17px;

      svg {
        transition: transform 0.2s;

        &.rotated {
          transform: rotate(180deg);
        }
      }
    }

    .timeline-content-wrapper {
      max-height: 500px;
      overflow-y: auto;
      transition: max-height 0.2s;
    }

    @media (min-width: 768px) {
      overflow-y: auto;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 16px;
    width: 90%;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    z-index: 2;
    flex: 0 0 auto;

    button {
      font-size: 18px;
    }

    .seemore {
      font-weight: 500;
      text-align: center;
      font-size: 14px;
      line-height: 100%;
      color: var(--theme_color);
      text-decoration: underline;

      @media (min-width: 575px) {
        display: none;
      }
    }

    @media (min-width: 768px) {
      max-width: 344px;
      width: 100%;
      margin-left: unset;
      margin-right: unset;
      padding: 0 20px;
    }
  }
}

.timeline-container {
  max-height: unset;
  overflow-y: hidden;
  overflow-x: hidden;
  padding: 8px 0 0;

  @media (min-width: 992px) {
    overflow-y: auto;
    max-height: calc(100vh - 300px);
    min-height: 320px;
  }
}

.timeline {
  display: flex;
  flex-direction: column;
  position: relative;
}

.timeline-item {
  display: flex;
  position: relative;
  margin-bottom: 15px;

  .timeline-marker {
    position: relative;
    width: 20px;
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    top: 3px;
  }

  .timeline-line {
    position: absolute;
    top: 10px;
    left: 7px;
    height: 100%;
    border-left: 3px dashed #ccc;
    content: "";
    margin: 2px 0;
  }

  .timeline-line.completed {
    background-color: #089B6B;
  }

  .timeline-line.active {
    border-color: #089B6B;
  }

  .timeline-dot {
    width: 16px;
    height: 16px;
    background-color: #FFFFFF;
    border-radius: 50%;
    border: 3px solid #CCCCCC;
    z-index: 1;

    &.active {
      border-color: #089B6B;
    }

    &.completed {
      background-color: #089B6B;

      &::after {
        content: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMCIgaGVpZ2h0PSI4IiB2aWV3Qm94PSIwIDAgMTAgOCIgZmlsbD0ibm9uZSI+CjxwYXRoIGQ9Ik04Ljc1IDAuNzVMMy4xNSA2Ljc1TDAuNzUgNC41IiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjEuNSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cjwvc3ZnPg==");
        position: absolute;
        top: -2px;

        @media (min-width: 375px) {
          top: -3px;
        }

        @media (min-width: 992px) {
          top: -5px;
        }
      }
    }
  }

  &:last-child {
    margin-bottom: 0;

    .timeline-line:not(.expanded) {
      height: 0;
    }
  }
}

.timeline-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 9px;
  padding-left: 15px;

  &__heading {
    gap: 1px;
    display: flex;
    flex-direction: column;
    cursor: pointer;

    &__container {
      display: flex;
      justify-content: space-between;
      align-items: center;

      svg {
        transition: transform 0.2s;

        &.rotated {
          transform: rotate(180deg);
        }
      }
    }
  }

  h3 {
    color: #163549;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    margin-bottom: 0;
  }

  .location {
    color: #163549;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    margin-bottom: 0;
  }

  .events {
    display: flex;
    flex-direction: column;
    gap: 4px;

    ul {
      padding: 0 15px;
      margin: 0;
    }
  }

  .event-time {
    color: #163549;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }

  .event-description {
    color: #4E6574;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
}

.map-container {
  width: 100%;
  height: 300px;
  border-radius: 0;
  overflow: hidden;
  position: relative;

  @media (min-width: 768px) {
    height: 100%;
    padding: 0;
  }
}

.map-container__location-marker {
  display: flex;
  flex-direction: column;
  height: 300px;
  color: var(--theme_color);
  position: absolute;
  z-index: 5;
  top: 0;
  left: 0;
  right: 0;
  align-items: center;
  padding: 10px 0 0;

  &:hover {
    background: unset;
  }

  @media (min-width: 768px) {
    height: 630px;
    padding: 100px 0 0;
  }

  svg {
    width: 250px;
    height: 120px;
    @media (min-width: 768px) {
      width: 350px;
      height: 150px;
    }
  }

  .marker-address {
    width: 200px;
    background: #FFFFFF;
    box-shadow: 0 4px 6px 2px rgba(0, 0, 0, 0.05);
    border-radius: 10px;
    margin-left: -70px;
    margin-bottom: 10px;
    padding: 15px;
    margin-right: 80px;

    @media (min-width: 768px) {
      margin-right: 200px;
      width: 250px;
    }

    .heading {
      font-weight: 500;
      font-size: 14px;
      line-height: 1.2;
      color: #163549;
      margin-bottom: 8px;

      @media (min-width: 575px) {
        font-size: 16px;
      }
    }

    .description {
      font-weight: 400;
      font-size: 12px;
      line-height: 100%;
      color: #4E6574;
      margin-bottom: 4px;

      @media (min-width: 575px) {
        font-size: 14px;
      }
    }

    .messages-items {
      padding-inline-start: 12px;
      max-height: 200px;
      overflow-y: auto;
      .message-item {
        padding: 6px 0;
        border-bottom: 1px solid #f0f0f0;

        &:last-child {
          border-bottom: none;
        }

        .heading {
          font-weight: 500;
        }
      }
    }
  }
}

#map {
  width: 100%;
  height: 100%;
}

.unset-height {
  max-height: unset !important;
}

.legacy-order-tracking {
  margin-top: 30px;

  .order-tracking {
    &__left-panel {

      @media (min-width: 992px) {
        height: auto;
      }
    }
  }

  .map-container {
    @media (min-width: 768px) {
      min-height: 635px;
      height: 100%;
    }
  }
}
</style>
